import { Icon } from "../Icon";
import { VoiceButton } from "../VoiceInput";
import RatingMatchQuestions from "../RatingMatchQuestions";
import { fillText } from "../../utils/leadDisplay";
import { DISPLAY_LABELS } from "../../shared/pricing/carCategory";
import { CAR_TYPES } from "../../utils/listChoices";

/* What the two pages a customer decides on share - a car's page (pages/CarDetail) and a
   hire driver's page (pages/AltingDriverProfile) - so they name a partner, show a
   rating, ask for one and say what Sramika is in the same words and the same look.
   Sizes are written at full design size; on a phone every button renders at four-fifths
   of it (index.css), and on a laptop the pages' .hm wrapper halves the pixel sizes. */

export const CARD = "bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_2px_10px_rgba(15,23,42,0.04)]";

// ── Words about the partner, in normal case ─────────────────────────────────
/* Names are stored as typed at onboarding, usually in capitals ("RAVI.K"). Shown in
   normal case, the record untouched - the rule the car cards follow. */
export const titleCase = (raw) => String(raw || "").trim().replace(/\s+/g, " ")
  .replace(/[A-Za-z]+/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
export const firstNameOf = (raw) => titleCase(raw).split(/[\s.,]+/).filter(Boolean)[0] || "";

/* The area, not the address: "Vijayawada Urban, Vidhyadharapuram, Vijayawada" →
   "Vidhyadharapuram, Vijayawada". A customer choosing a partner needs to know roughly
   where they start from; a partner's house number is not theirs to read. Parts with a
   number in them (house numbers, pin codes) and the state and country are left out,
   repeats are dropped, and the last two parts are kept. */
const NOT_AN_AREA = /^(india|andhra pradesh|telangana|tamil nadu|karnataka|ap|ts)$/i;
export function areaOf(address) {
  const seen = new Set();
  const parts = String(address || "").split(",").map((p) => p.trim())
    .filter((p) => p && !/\d/.test(p) && !NOT_AN_AREA.test(p))
    .filter((p) => { const k = p.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
  return parts.slice(-2).join(", ");
}

/* The car types a hire driver can drive, in the list's words. The saved codes
   (SEDAN_5_STD, SUV_7_LUX, …) are collapsed to the rider-facing type through the shared
   DISPLAY_LABELS map, repeats dropped and order kept, and "MPV" stays "MPV" - its list
   name says 7 seats. The seat sizes ("Tempo Traveller (12 Seater)", "Mini Bus", "Bus (26
   Seater)") are their family's word, so they are said in the reader's language like the
   rest instead of falling through in English. Display only; the record is untouched. */
export function driveTypeWords(codes, t) {
  const out = [];
  (codes || []).forEach((code) => {
    const label = DISPLAY_LABELS[code] || String(code || "").replace(/_/g, " ").trim();
    if (!label) return;
    const base = label.replace(/\s*\([^)]*\)\s*$/, "").toLowerCase();
    const opt = CAR_TYPES.find((o) => o.value.toLowerCase() === label.toLowerCase())
      || CAR_TYPES.find((o) => o.value.toLowerCase() === base)
      || CAR_TYPES.find((o) => base.endsWith(" " + o.value.toLowerCase()));
    const word = opt ? (opt.value === "MPV" ? t("cv_mpv") : t(opt.key)) : label;
    if (!out.includes(word)) out.push(word);
  });
  return out;
}

// A star, filled or empty.
export const StarGlyph = ({ on, size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true"
    fill={on ? "#F59E0B" : "none"} stroke={on ? "#F59E0B" : "#CBD5E1"} strokeWidth="1.6" strokeLinejoin="round">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

/* The parts of a rating, in the words the list's filters use for them. A car is rated
   on five; a hire driver on four, one of them the driving itself. */
export const PART_WORDS = {
  punctuality: "hm_tOnTime", driverBehaviour: "hm_tPolite", rideComfort: "hm_tComfort",
  cleanliness: "hm_tClean", safety: "hm_tSafe", drivingQuality: "dp_pDriving",
};
const PART_ICONS = {
  punctuality: "clock", driverBehaviour: "user", rideComfort: "car", cleanliness: "zap",
  safety: "shield", drivingQuality: "navigation",
};

/* Whether what came was what was chosen, as customers answered it with their stars:
   "The same car you booked came - 9 of 10". */
const MATCH_ROWS = [["sameCar", "car", "rm_carCame"], ["sameDriver", "user", "rm_driverCame"]];

/* The parts of a rating to give, one row of stars each - the car page's form, Home's
   "Rate your trip", the enquiry sheet and the WhatsApp link page (mock Rate5_1) ask the
   same way. `rate` is useLeadRating's result (or anything with parts and setPart). */
export function PartRows({ t, rate, partKeys }) {
  if (!partKeys?.length) return null;
  return (
    <>
      <p className="mt-2.5 text-[9.5px] font-semibold text-[#64748B]">{t("cv_rateParts")}</p>
      <ul className="mt-1">
        {partKeys.map((k) => (
          <li key={k} className="flex items-center gap-2">
            <span className="flex-1 min-w-0 text-[10.5px] font-semibold text-[#334155] leading-tight">{t(PART_WORDS[k] || k)}</span>
            <span className="flex shrink-0">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => rate.setPart(k, n)} aria-label={`${t(PART_WORDS[k] || k)}: ${n} ★`}
                  className="w-[34px] h-[34px] flex items-center justify-center">
                  <StarGlyph on={(rate.parts?.[k] || 0) >= n} size={18} />
                </button>
              ))}
            </span>
          </li>
        ))}
      </ul>
    </>
  );
}

/* The stars, how many gave them, and each part in whole words. parts: [{ k, v }] with
   v > 0. match: the server's matchStats - { sameCar, sameDriver: { asked, yes } }; a
   question nobody answered is not shown.
   allKeys (a car, mock Rate5_1 change 1): every part always shown - "-" for one nobody
   rated, "Parts from 5 of 7 ratings" when some ratings gave stars only, and with no
   rating at all the five empty rows and "rate it after your trip". Never made up. */
export function RatingsSummary({ t, avg, count, parts, match, allKeys = null, partsFrom = null }) {
  const ratingsWord = Number(count) === 1 ? t("cv_oneRating") : fillText(t("cv_nRatings"), { n: count });
  const none = !(Number(count) > 0) || avg === null || avg === undefined;
  if (allKeys) {
    const byKey = Object.fromEntries((parts || []).map((p) => [p.k, p.v]));
    parts = allKeys.map((k) => ({ k, v: byKey[k] || null }));
  }
  const matchRows = MATCH_ROWS.map(([k, icon, key]) => ({ k, icon, key, asked: Number(match?.[k]?.asked) || 0, yes: Number(match?.[k]?.yes) || 0 }))
    .filter((r) => r.asked > 0);
  return (
    <section className={`${CARD} p-3`}>
      {none ? (
        <p className="text-[13px] font-extrabold text-[#0F172A]">{t("cv_noRatings")}</p>
      ) : (
        <p className="flex items-center gap-1 text-[13px] font-extrabold text-[#0F172A]">
          <StarGlyph on size={16} />{Number(avg).toFixed(1)}
          <span className="text-[11px] font-medium text-[#64748B]">· {ratingsWord}</span>
        </p>
      )}
      {parts.length > 0 && (
        <ul className="mt-2 grid grid-cols-2 gap-1.5">
          {parts.map(({ k, v }) => (
            <li key={k} className="min-h-[34px] rounded-[10px] bg-[#F8FAFC] border border-[#EEF2F7] px-2 py-1 flex items-center gap-1.5 text-[10.5px] font-semibold text-[#334155]">
              <Icon name={PART_ICONS[k] || "star"} size={13} stroke="#5E8700" strokeWidth={2} className="shrink-0" />
              <span className="flex-1 min-w-0 leading-tight">{t(PART_WORDS[k] || k)}</span>
              <b className={`shrink-0 font-extrabold ${v ? "text-[#5E8700]" : "text-[#CBD5E1]"}`}>{v ? Number(v).toFixed(1) : "-"}</b>
            </li>
          ))}
        </ul>
      )}
      {allKeys && (none
        ? <p className="mt-1.5 text-[10.5px] text-[#64748B]">{t("cv_rateAfter")}</p>
        : partsFrom !== null && partsFrom < Number(count) && <p className="mt-1.5 text-[10.5px] text-[#64748B]">{fillText(t("cv_partsFrom"), { n: partsFrom, m: count })}</p>)}
      {matchRows.length > 0 && (
        <ul className={`${parts.length > 0 ? "mt-1.5" : "mt-2"} space-y-1.5`}>
          {matchRows.map((r) => (
            <li key={r.k} className="min-h-[34px] rounded-[10px] bg-[#F8FAFC] border border-[#EEF2F7] px-2 py-1 flex items-center gap-1.5 text-[10.5px] font-semibold text-[#334155]">
              <Icon name={r.icon} size={13} stroke="#5E8700" strokeWidth={2} className="shrink-0" />
              <span className="flex-1 min-w-0 leading-tight">{t(r.key)}</span>
              {/* Green when nearly everyone said yes; amber when some said another came. */}
              <b className={`shrink-0 font-extrabold ${r.yes >= r.asked * 0.8 ? "text-[#5E8700]" : "text-[#B45309]"}`}>
                {fillText(t("rm_of"), { yes: r.yes, n: r.asked })}
              </b>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* Rate - the recent-enquiry card's stars and feedback, with the parts pre-filled from
   the stars. For a customer who called this partner; `rate` is useLeadRating's result.
   Once rated: the thanks, and Change to open it again. */
export function RateCard({ t, rate, name, partKeys }) {
  const showForm = !rate.myRating || rate.editing;
  return (
    <section className={`${CARD} p-3`}>
      {!showForm ? (
        <div className="flex items-center gap-2">
          <StarGlyph on size={18} />
          <p className="flex-1 min-w-0 text-[11px] font-bold text-[#3F5E00]">{fillText(t("ld_rateThanks"), { n: rate.myRating })}</p>
          <button type="button" onClick={rate.edit} className="h-[36px] px-3 shrink-0 rounded-[10px] border border-[#CBD5E1] bg-white text-[11px] font-bold text-[#334155] active:scale-[0.98]">
            {t("cv_change")}
          </button>
        </div>
      ) : (
        <>
          <p className="text-[12px] font-extrabold text-[#0F172A] leading-snug">
            {name ? fillText(t("cv_rateTitle"), { name }) : t("cv_rateNoName")}
          </p>
          <div className="mt-2 flex gap-1.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => rate.pick(n)} aria-label={`${n} ★`} aria-pressed={rate.stars >= n}
                className={`w-[46px] h-[46px] rounded-[12px] border flex items-center justify-center active:scale-95 transition-transform ${rate.stars >= n ? "bg-[#FFFBEB] border-[#FCD34D]" : "bg-white border-[#E5E7EB]"}`}>
                <StarGlyph on={rate.stars >= n} size={22} />
              </button>
            ))}
          </div>
          {rate.stars > 0 && (
            <>
              <PartRows t={t} rate={rate} partKeys={partKeys} />
              {/* Same car, same driver - one tap each, and either can be left. */}
              <RatingMatchQuestions t={t} rate={rate} />
              <div className="mt-2.5 flex gap-2">
                <input className="ctl flex-1 min-w-0 h-[42px] rounded-[11px] border border-[#E5E7EB] bg-white px-3 text-[11px] text-[#0F172A] placeholder-[#94A3B8] outline-none focus:border-[#7BAE00]"
                  placeholder={t("ld_ratePh")} maxLength={300} value={rate.review} onChange={(e) => rate.setReview(e.target.value)} />
                <VoiceButton where="feedback_detail" className="w-[42px] h-[42px]" onText={(v) => rate.setReview(`${rate.review ? `${rate.review} ` : ""}${v}`.slice(0, 300))} />
                <button type="button" onClick={rate.send} disabled={rate.busy}
                  className="shrink-0 h-[42px] px-3.5 rounded-[11px] bg-[#7BAE00] text-white text-[11px] font-bold active:scale-[0.98] disabled:opacity-60">
                  {rate.busy ? "…" : t("ld_rateSend")}
                </button>
              </div>
              {rate.error && <p className="mt-1.5 text-[10px] font-semibold text-[#DC2626]">{rate.error}</p>}
            </>
          )}
        </>
      )}
    </section>
  );
}

/* What is true of every partner here: you talk to them directly, nothing is booked so
   there is no booking fee, and Sramika helps when needed. directKey names who you talk
   to - the owner of a car, or the driver. */
export function TrustLine({ t, directKey = "cv_trustDirect" }) {
  return (
    <section className={`${CARD} p-3 grid grid-cols-3 gap-2`}>
      {[["phone", directKey], ["rupee", "cv_trustNoFee"], ["shield", "cv_trustHelp"]].map(([icon, key]) => (
        <div key={key} className="flex flex-col items-center text-center">
          <span className="w-[32px] h-[32px] rounded-full bg-[#F1F7E3] flex items-center justify-center">
            <Icon name={icon} size={15} stroke="#5E8700" strokeWidth={2} />
          </span>
          <p className="mt-1.5 text-[9.5px] font-bold text-[#334155] leading-snug">{t(key)}</p>
        </div>
      ))}
    </section>
  );
}
